import * as z from 'zod';
import { isoDateTimeSchema, strict, uuidSchema } from './common.js';
import { pageSchema } from './pagination.js';
/**
 * AD-34: an API key sees the least it can, by default.
 *
 * A key is the easiest bulk-exfiltration path in this system, so the safe default is the quiet
 * one: without `allowPii` a key sees ids, timestamps, presence state and counts and nothing else.
 * Every key carries scopes, an expiry and a rate limit, every call is logged with how many records
 * it returned, and that count is what the exfiltration alert of AD-37 watches.
 *
 * **Three things are deliberately absent from this contract.**
 *
 * There is no IP allowlist. It is a weaker control than the scoping and expiry already in place, it
 * is miserable against dynamic addresses, and its real cost lands in the generated scope matrix.
 *
 * There is no scope for backup, restore, shred, user administration, settings, machine credentials,
 * band batches or WRITING a field definition, and there never will be. A key cannot reach those
 * routes because no scope names them, which is a stronger statement than a check somebody has to
 * remember to write. `fields:read` survives alone in that family: the field registry is the SCHEMA
 * an integration must read to send a valid person, and reading it is not administering it.
 *
 * There is no way to read a key's secret back. {@link apiKeySchema} has no field for it, so a
 * response cannot carry one even by accident; the secret exists in exactly one response, the one
 * that created the key.
 *
 * **And there is no `version`, which is a correction Phase 20 made rather than an omission.** An
 * earlier draft of this file carried one on {@link apiKeySchema} and demanded one on
 * {@link revokeApiKeyRequestSchema}; `api_keys` has no such column and Section 1.5 declares none,
 * so the field validated and then did nothing — the defect this project refuses everywhere else
 * (`createPersonRequestSchema.imageIds`, `voidCredentialBlankRequestSchema`). A version guard
 * would buy nothing here either: revocation is the only mutation, it is terminal, and two
 * administrators revoking the same key concurrently both get the state they asked for. The revoke
 * request is therefore a strict EMPTY object, which REFUSES a caller that still sends a version
 * rather than accepting and dropping it.
 */
/** The scope catalogue, as data. A route names the scope it needs; the matrix test proves it. */
export const API_KEY_SCOPES = [
    'analytics:read',
    'audit:read',
    'exports:read',
    'fields:read',
    'persons:read',
    'persons:write',
    'presence:read',
    'presence:write',
];
export const apiKeyScopeSchema = z.enum(API_KEY_SCOPES);
export const apiKeyStatusSchema = z.enum(['active', 'revoked']);
/** Ninety days unless the creator says otherwise, so a forgotten key expires on its own. */
export const DEFAULT_API_KEY_EXPIRY_DAYS = 90;
/** The largest expiry anybody may ask for. A key that never expires is a credential nobody rotates. */
export const MAX_API_KEY_EXPIRY_DAYS = 365;
/** A key as the API returns it. Note what is not here: the secret, in any form. */
export const apiKeySchema = strict({
    id: uuidSchema,
    name: z.string().trim().min(1).max(120),
    /** The searchable, non-secret half, used to find the row before the constant-time compare. */
    prefix: z.string().regex(/^[a-z0-9]{8,16}$/u),
    scopes: z.array(apiKeyScopeSchema).min(1),
    allowPii: z.boolean(),
    rateLimitPerMinute: z.int().min(1).max(6000),
    expiresAt: isoDateTimeSchema,
    status: apiKeyStatusSchema,
    lastUsedAt: isoDateTimeSchema.nullable(),
    requestCount: z.int().nonnegative(),
    createdAt: isoDateTimeSchema,
    createdBy: uuidSchema.nullable(),
    revokedAt: isoDateTimeSchema.nullable(),
});
export const createApiKeyRequestSchema = strict({
    name: z.string().trim().min(1).max(120),
    scopes: z
        .array(apiKeyScopeSchema)
        .min(1, { error: 'errors.validation.required' })
        .refine((scopes) => new Set(scopes).size === scopes.length, {
        error: 'errors.validation.invalid_option',
    }),
    /** Off unless an administrator deliberately turns it on, which is the whole of AD-34. */
    allowPii: z.boolean().default(false),
    rateLimitPerMinute: z.int().min(1).max(6000).default(60),
    expiresInDays: z.int().min(1).max(MAX_API_KEY_EXPIRY_DAYS).default(DEFAULT_API_KEY_EXPIRY_DAYS),
});
/** The one response that carries a secret, returned once and never reconstructible. */
export const createdApiKeySchema = strict({
    apiKey: apiKeySchema,
    secret: z.string().min(32).max(128),
});
/**
 * Withdrawing a key. It carries nothing, and the empty object is the statement.
 *
 * Strict and empty rather than absent, so a caller that sends a `version` — or a `reason`, which
 * `api_keys` has no column for — is REFUSED rather than having it silently dropped. See the module
 * note for why there is no version to guard on.
 */
export const revokeApiKeyRequestSchema = strict({});
/** One line of the usage log. `recordsReturned` is what the exfiltration alert counts (AD-37). */
export const apiKeyUsageSchema = strict({
    id: z.string(),
    keyId: uuidSchema,
    occurredAt: isoDateTimeSchema,
    route: z.string().max(200),
    method: z.string().max(10),
    status: z.int(),
    durationMs: z.int().nonnegative(),
    bytesOut: z.int().nonnegative(),
    recordsReturned: z.int().nonnegative(),
});
/** One page of keys. Paged like every other list route, so `limit` and `cursor` mean one thing. */
export const apiKeyPageSchema = pageSchema(apiKeySchema);
/** One page of the usage log, newest first. What the API panel of Section 1.12 reads. */
export const apiKeyUsagePageSchema = pageSchema(apiKeyUsageSchema);
//# sourceMappingURL=api-key.js.map